import ImageUploadBaseController from "./image_upload_base_controller"

// Section image upload controller - extends ImageUploadBaseController
// Usage: data-controller="section-image-upload"
// Specifically designed for nested form sections (about sections, etc.)
export default class extends ImageUploadBaseController {
  static targets = [
    ...ImageUploadBaseController.targets,
    "previewImage",
    "placeholderWrapper",
    "removeImageSection",
    "recropButton"
  ]

  connect() {
    super.connect()
    this.handleInitialImage()
    
    // Add cancel detection for file input
    if (this.hasUploadFieldTarget) {
      // Listen for focus events to detect when file dialog is opened/closed
      this.uploadFieldTarget.addEventListener('focus', this.onFileInputFocus.bind(this))
      this.uploadFieldTarget.addEventListener('blur', this.onFileInputBlur.bind(this))
    }
    
    // Check if this is a newly added section that should auto-trigger file selector
    this.checkAutoTrigger()
  }

  // Override: Custom cropper options for section images
  getCropperOptions() {
    return {
      aspectRatio: 16/9, // Good for content images
      viewMode: 2,
      autoCropArea: 0.9,
      responsive: true,
      background: true,
      zoomable: true,
      scalable: true,
      rotatable: true,
      checkOrientation: false,
      modal: true,
      guides: true,
      center: true,
      highlight: true,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: true
    }
  }

  // Override: Handle post-crop actions for sections
  onImageCropped(croppedImageURL, croppedFile) {
    this.displayCroppedImage(croppedImageURL)
    this.hideUploadArea()
    this.hidePlaceholderArea()
    this.showActionsArea()
    this.updatePreviewImage(croppedImageURL)
    this.showRecropButton() // Show re-crop button since image was just cropped
    
    // Note: originalImageBlob is already stored when user first selected the file
    // We keep the original for re-cropping, not the cropped version
  }

  // Override: Handle image removal for sections  
  onImageRemoved() {
    this.hideActionsArea()
    this.showUploadArea()
    this.clearPreviewImage()
  }

  // Handle initial image if present (for editing existing sections)
  handleInitialImage() {
    if (this.hasUploadFieldTarget && this.hasPreviewImageTarget) {
      // Check if there's already an image attached
      const existingImage = this.previewImageTarget.src
      if (existingImage && existingImage !== window.location.href) {
        this.hideUploadArea()
        this.hidePlaceholderArea()
        this.showActionsArea()
      }
    }
  }

  // Check if this section was just added and should auto-trigger file selector
  checkAutoTrigger() {
    // Use a timeout to ensure the DOM is fully ready and Cocoon has finished adding the section
    setTimeout(() => {
      // Check if this is a new section without an image
      const hasNoImage = !this.hasPreviewImageTarget || !this.previewImageTarget.src || this.previewImageTarget.src === window.location.href || this.previewImageTarget.src === ''
      
      if (this.hasUploadFieldTarget && hasNoImage) {
        // Look for the data attribute on the add association button that was clicked
        const addImageButton = document.querySelector('[data-auto-trigger-file-selector="true"]')
        
        // If we find the button and this is the most recently added image section
        if (addImageButton && this.isLastImageSection()) {
          this.triggerImageInput()
          // Remove the data attribute to prevent future triggers for THIS section
          // but don't remove it globally so other new sections can use it
          addImageButton.removeAttribute('data-auto-trigger-file-selector')
          
          // Re-add the attribute after a delay so future sections can use it
          setTimeout(() => {
            addImageButton.setAttribute('data-auto-trigger-file-selector', 'true')
          }, 500)
        }
      }
    }, 150) // Slightly longer delay to ensure Cocoon has finished
  }

  // Check if this is the most recently added image section
  isLastImageSection() {
    const allImageSections = document.querySelectorAll('[data-section-type="image"]')
    const lastImageSection = allImageSections[allImageSections.length - 1]
    return lastImageSection === this.element
  }

  // Display cropped image in the preview area
  displayCroppedImage(imageURL) {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = imageURL
      this.previewImageTarget.classList.remove('d-none')
    }
  }

  // Update preview image
  updatePreviewImage(imageURL) {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = imageURL
    }
  }

  // Clear preview image
  clearPreviewImage() {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = ''
      this.previewImageTarget.classList.add('d-none')
    }
  }

  // Override: Custom error display for sections
  showError(message) {
    // Create a more user-friendly error display
    this.clearExistingErrors()
    
    const errorElement = document.createElement('div')
    errorElement.className = 'alert alert-danger mt-2 section-image-error'
    errorElement.textContent = message
    
    if (this.hasUploadWrapperTarget) {
      this.uploadWrapperTarget.insertAdjacentElement('afterend', errorElement)
    }
    
    // Auto-remove error after 5 seconds
    setTimeout(() => this.clearExistingErrors(), 5000)
  }

  clearExistingErrors() {
    const existingErrors = document.querySelectorAll('.section-image-error')
    existingErrors.forEach(error => error.remove())
  }

  // Placeholder area management
  showPlaceholderArea() {
    if (this.hasPlaceholderWrapperTarget) {
      this.placeholderWrapperTarget.classList.remove('d-none')
    }
  }

  hidePlaceholderArea() {
    if (this.hasPlaceholderWrapperTarget) {
      this.placeholderWrapperTarget.classList.add('d-none')
    }
  }

  // Re-crop button management
  showRecropButton() {
    if (this.hasRecropButtonTarget) {
      this.recropButtonTarget.classList.remove('d-none')
    }
  }

  hideRecropButton() {
    if (this.hasRecropButtonTarget) {
      this.recropButtonTarget.classList.add('d-none')
    }
  }

  // Re-crop current image using the original file
  recropCurrentImage() {
    if (this.originalImageFile) {
      // Create object URL from the ORIGINAL image file and trigger cropping
      const imageURL = URL.createObjectURL(this.originalImageFile)
      if (this.hasImagePreviewTarget) {
        this.imagePreviewTarget.src = imageURL
        this.openCropModal()
        this.initializeCropper()
      }
    } else if (this.hasPreviewImageTarget && this.previewImageTarget.src) {
      // Fallback: use current preview image (for existing saved images)
      if (this.hasImagePreviewTarget) {
        this.imagePreviewTarget.src = this.previewImageTarget.src
        this.openCropModal()
        this.initializeCropper()
      }
    }
  }

  // Override file input handling to detect cancellation and store original image
  handleImageSelect(event) {
    const input = event.target
    const file = input.files[0]

    if (!file) {
      // User cancelled file selection
      this.handleFileSelectionCancelled()
      return
    }

    // Store original image for re-cropping later
    this.originalImageFile = file
    
    // Call parent method for normal processing
    super.handleImageSelect(event)
  }

  // Handle when user cancels file selection
  handleFileSelectionCancelled() {
    console.log('File selection cancelled')
    
    // Check if this is a new section with no existing image
    const hasNoExistingImage = !this.hasPreviewImageTarget || 
                               !this.previewImageTarget.src || 
                               this.previewImageTarget.src === window.location.href ||
                               this.previewImageTarget.src === ''
    
    if (hasNoExistingImage) {
      // No existing image, remove this section
      setTimeout(() => this.removeAssociation(), 100) // Small delay to ensure proper cleanup
    }
  }

  // Remove the association (trigger Cocoon's remove)
  removeAssociation() {
    
    if (this.hasRemoveImageSectionTarget) {
      this.removeImageSectionTarget.click()
    } else {
      const removeLink = this.element.querySelector('.remove-section-btn, [data-association="remove"], a[data-confirm]')
      if (removeLink) {
        removeLink.click()
      } else {
        this.element.remove()
      }
    }
  }

  // File input focus/blur handlers for better cancel detection
  onFileInputFocus() {
    this.fileDialogOpened = true
  }

  onFileInputBlur() {
    if (this.fileDialogOpened) {
      // Give a short delay to see if a file was actually selected
      setTimeout(() => {
        if (!this.uploadFieldTarget.files.length) {
          this.handleFileSelectionCancelled()
        }
        this.fileDialogOpened = false
      }, 100)
    }
  }

  // Public methods for external control
  openFileSelector() {
    this.triggerImageInput()
  }

  reset() {
    this.removeImage()
  }

  disconnect() {
    super.disconnect()
    this.clearExistingErrors()
  }
}